import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { MultiGrid, AutoSizer } from 'react-virtualized';

import FloatInput from './FloatInput';
import VenueTooltip from './VenueTooltip';

import { formatAndRoundMoney, formatFigure, formatSeparateByThousands } from 'Helpers/formatService';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import { HelpCircleIcon } from 'CommonComponents/icons';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import ArrowPathIcon from 'Assets/icons/arrow-path.svg';
import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import TrashIcon from 'Assets/icons/trash.svg';

// Column geometry. Original markup used tw-min-w-128 (32rem ≈ 512px) for expense columns
// and tw-w-8 (32px) for the vertical divider strips.
const EXPENSE_COLUMN_WIDTH = 512;
const DIVIDER_COLUMN_WIDTH = 40;
const VENUE_COLUMN_WIDTH = 512; // fixed left column (venue name + venue totals)

// Row geometry. Rows are venues here, so every body row is the same height; size it from the
// expense with the most quantity slots. Constants match the rendered content with a small
// safety margin — tall enough to never clip under tw-overflow-hidden, but not so tall as to
// leave large gaps (the venues view originally over-estimated and the rows rendered oversized).
// Expense header = description (≤2-line wrap budget) + price + the three totals rows; ~120px.
const HEADER_ROW_HEIGHT = 150; // expense header (description ≤2 lines + price + totals)
const CELL_V_PADDING = 12;
const ROW_BLOCK_GAP = 8;
const CHECKBOX_ROW = 24;
const SLOT_LABEL = 16; // tw-text-xs label line (~16px). The expenses view renders NO link control.
const SLOT_INPUT = 28;
const SLOT_GAP = 4;
const PRICE_ROW = SLOT_LABEL + SLOT_INPUT + SLOT_GAP;
const TOTALS_BLOCK = 44; // label (~16) + gap + value (~16)
const MIN_BODY_ROW_HEIGHT = 100;

export default function AssignTableExpenses(props) {
    const [tooltipParentEl, setTooltipParentEl] = useState();

    const tableWrapperEl = useCallback((node) => {
        node && setTooltipParentEl(node);
    }, []);

    const gridRef = useRef();

    const showAnyTotals = props.showTotal || props.showUncommitted || props.showCommitted;

    const expenses = props.expenses;
    const venues = props.venues;

    // Column model: the fixed venue-name column (index 0) plus one column per expense — including
    // divider columns (rendered as narrow vertical charcoal strips). Collapsed / deleted expense
    // columns are dropped entirely so the virtualised window stays correct (risks R3/R4). Each
    // retained column carries its ORIGINAL expenses index for totals + handleQuantityChange.
    const columns = useMemo(() => {
        const result = [];
        for (let expenseIndex = 0; expenseIndex < expenses.length; expenseIndex++) {
            const expense = expenses[expenseIndex];
            if (!expense || expense.deleted) continue;

            const isDivider = expense.type === 'template_divider';
            if (!isDivider && props.showExpense && props.showExpense[expenseIndex] === false) continue;

            result.push({ expense, expenseIndex, isDivider });
        }
        return result;
    }, [expenses, props.showExpense]);

    const columnCount = columns.length + 1; // +1 fixed venue-name column
    const rowCount = venues.length + 1; // +1 fixed expense header row

    // Constant body-row height, sized from the expense with the most quantity slots.
    const bodyRowHeight = useMemo(() => {
        let maxSlots = 0;
        for (const col of columns) {
            if (col.isDivider) continue;
            const slots = Array.isArray(col.expense.amount) ? col.expense.amount.filter((q) => q && q.name).length : 0;
            maxSlots = Math.max(maxSlots, slots);
        }

        let height = CELL_V_PADDING;
        if (props.selectable && props.editableTable) {
            height += CHECKBOX_ROW + ROW_BLOCK_GAP;
        }
        height += maxSlots * (SLOT_LABEL + SLOT_INPUT + SLOT_GAP + ROW_BLOCK_GAP);
        height += PRICE_ROW + ROW_BLOCK_GAP;
        if (showAnyTotals) {
            height += TOTALS_BLOCK + ROW_BLOCK_GAP;
        }
        return Math.max(MIN_BODY_ROW_HEIGHT, Math.ceil(height));
    }, [columns, props.selectable, props.editableTable, showAnyTotals]);

    const getRowHeight = useCallback(({ index }) => (index === 0 ? HEADER_ROW_HEIGHT : bodyRowHeight), [bodyRowHeight]);

    const getColumnWidth = useCallback(
        ({ index }) => {
            if (index === 0) return VENUE_COLUMN_WIDTH;
            const col = columns[index - 1];
            return col && col.isDivider ? DIVIDER_COLUMN_WIDTH : EXPENSE_COLUMN_WIDTH;
        },
        [columns],
    );

    // Recompute grid geometry when row/column count or heights change (risk R4).
    useEffect(() => {
        if (gridRef.current) {
            gridRef.current.recomputeGridSize();
        }
    }, [columns, venues.length, bodyRowHeight, props.showTotal, props.showUncommitted, props.showCommitted]);

    const renderHeaderParentCell = (key, style) => (
        <div key={key} style={style} className="tw-flex tw-flex-col tw-p-1.5 tw-overflow-hidden tw-bg-blue-25 tw-border-r-2 tw-border-grey">
            <div className={`tw-grid tw-gap-4 ${showAnyTotals ? 'tw-grid-cols-4 tw-mb-4' : 'tw-grid-cols-3'}`}>
                {showAnyTotals && <div></div>}
                <div className="tw-col-span-3">
                    <div className="tw-flex tw-justify-center tw-gap-x-2">
                        <a className="tw-text-blue hover:tw-text-blue-75" href="#">
                            {props.parentName}
                        </a>
                        <button onClick={(e) => props.handleRotate()}>
                            <ArrowPathIcon className="tw-w-2.5 tw-h-2.5" />
                        </button>
                    </div>
                </div>
            </div>
            {showAnyTotals && (
                <>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                        <div></div>
                        <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                            <div>{$t('Budget')}</div>
                        </div>

                        <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                            <div>{$t('Uncommitted')}</div>
                        </div>

                        <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                            <div>{$t('Committed')}</div>
                        </div>
                    </div>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                        <div>{$t('Total')}:</div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.budget.grand, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.uncommitted.grand, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.committed.grand, formatAndRoundMoney)}</span>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const renderHeaderDividerCell = (key, style, expense, expenseIndex) => (
        <div
            key={key}
            style={style}
            className="tw-z-20 tw-cursor-pointer !tw-bg-charcoal-50 tw-overflow-hidden tw-border-r-2 tw-border-grey"
            onClick={() => props.toggleVisibility(expense)}
        >
            <div className="tw-h-full tw-whitespace-nowrap tw-flex tw-flex-col tw-items-center tw-justify-start tw-pt-1.5 tw-gap-y-2 tw-text-white">
                <div className="tw-font-medium [orientation:mixed] [writing-mode:vertical-lr]">{expense.name}</div>
                <div className="tw-shrink-0">
                    {props.showExpense[expenseIndex + 1] ? (
                        <ChevronLeftIcon className="tw-h-2.5 tw-w-2.5" />
                    ) : (
                        <ChevronRightIcon className="tw-h-2.5 tw-w-2.5" />
                    )}
                </div>
            </div>
        </div>
    );

    const renderHeaderExpenseCell = (key, style, expense, expenseIndex) => (
        <div key={key} style={style} className="tw-flex tw-flex-col tw-p-1.5 tw-overflow-hidden tw-bg-blue-25 tw-border-r-2 tw-border-grey">
            <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full tw-mb-4">
                <div className="tw-font-bold">{$t('Description')}:</div>
                <div className="tw-col-span-3 tw-flex tw-flex-row tw-items-center tw-w-full">
                    {props.selectable && props.editableTable && !props.forProcurement && !props.finalStates.expenses[expense.id].isDisabled && (
                        <CheckboxInput
                            id={expense.id}
                            checked={props.finalStates.expenses[expense.id].isSelected}
                            label=""
                            onChange={() => props.toggleFinalState('expense', null, expense.id)}
                        />
                    )}
                    <div className="tw-text-blue hover:tw-text-blue-75 tw-font-semibold">{expense.name}</div>
                </div>
            </div>
            <div className={`tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full ${showAnyTotals && 'tw-mb-4'}`}>
                <div className="tw-font-bold">{$t('Price')}:</div>
                <div className="tw-col-span-3 tw-flex tw-flex-row tw-items-center tw-w-full">
                    <div>{formatFigure(expense.price, formatSeparateByThousands, { precision: 3 })}</div>
                </div>
            </div>
            {showAnyTotals && (
                <>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                        <div></div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Budget')}</div>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Uncommitted')}</div>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Committed')}</div>
                            </div>
                        )}
                    </div>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                        <div className="tw-font-bold">{$t('Total')}:</div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.expenses[expenseIndex].budget.total, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.expenses[expenseIndex].uncommitted.total, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.expenses[expenseIndex].committed.total, formatAndRoundMoney)}</span>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const renderVenueFixedCell = (key, style, venue) => (
        <div key={key} style={style} className="tw-flex tw-flex-col tw-p-1.5 tw-overflow-hidden tw-bg-[#f1f1f1] tw-border-r-2 tw-border-t-2 tw-border-grey">
            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                <div className={`tw-grid tw-gap-4 tw-w-full tw-mb-4 ${showAnyTotals ? 'tw-grid-cols-4' : 'tw-grid-cols-3'}`}>
                    {showAnyTotals && <div></div>}

                    <div className="tw-col-span-3 tw-flex tw-flex-row tw-justify-center tw-items-center tw-gap-x-2 tw-w-full">
                        {props.selectable && props.editableTable && !props.finalStates.venues[venue.id].isDisabled && (
                            <CheckboxInput
                                id={venue.id}
                                checked={props.finalStates.venues[venue.id].isSelected}
                                label=""
                                onChange={() => props.toggleFinalState('parentVenue', venue.id)}
                            />
                        )}
                        <Tooltip
                            placement="right"
                            tooltipType="content"
                            backgroundClass="tw-bg-white"
                            tooltipContent={<VenueTooltip venue={venue} />}
                            parentEl={tooltipParentEl}
                        >
                            {window.profitLossUrl ? (
                                <a className="tw-text-blue hover:tw-text-blue-75" href={window.profitLossUrl.replace('VENUE_ID', venue.slug)}>
                                    {venue.name}
                                </a>
                            ) : (
                                <div>{venue.name}</div>
                            )}
                        </Tooltip>
                    </div>
                </div>
                {showAnyTotals && (
                    <>
                        <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full">
                            <div></div>
                            {props.showTotal && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                    <div>{$t('Budget')}</div>
                                </div>
                            )}
                            {props.showUncommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                    <div>{$t('Uncommitted')}</div>
                                </div>
                            )}
                            {props.showCommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                    <div>{$t('Committed')}</div>
                                </div>
                            )}
                        </div>
                        <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full">
                            <div className="tw-font-bold">{$t('Total')}:</div>
                            {props.showTotal && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.venues, venue.id) ? props.totals.venues[venue.id].budget : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                            {props.showUncommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.venues, venue.id)
                                                ? props.totals.venues[venue.id].uncommitted
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                            {props.showCommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.venues, venue.id) ? props.totals.venues[venue.id].committed : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    const renderExpenseDataCell = (key, style, expense, expenseIndex, venue) => {
        let assignedValues = JSON.parse('{}');
        let expenseableId = null;
        const selectedVenue = typeof expense.venues.find((v) => v.id == venue.id) !== 'undefined' ? expense.venues.find((v) => v.id == venue.id) : null;
        if (selectedVenue) {
            const pivot = typeof selectedVenue.pivot !== 'undefined' ? selectedVenue.pivot : null;
            if (pivot) {
                assignedValues = JSON.parse(typeof pivot.uncommitted_amount !== 'undefined' ? pivot.uncommitted_amount : '{}');
                expenseableId = typeof pivot.id !== 'undefined' ? pivot.id : null;
            }
        }

        return (
            <div key={key} style={style} className="tw-p-1.5 tw-overflow-hidden tw-bg-[#f1f1f1] tw-border-r-2 tw-border-t-2 tw-border-grey">
                <div className="tw-space-y-2">
                    {props.selectable &&
                        props.editableTable &&
                        props.finalStates.venues[venue.id].isSelected &&
                        props.finalStates.expenses[expense.id].isSelected &&
                        Object.prototype.hasOwnProperty.call(props.finalStates.expenses[expense.id].venues, venue.id) &&
                        !props.forProcurement &&
                        !props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled && (
                            <CheckboxInput
                                id={expense.id}
                                checked={props.finalStates.expenses[expense.id].venues[venue.id].isSelected}
                                label=""
                                onChange={() => props.toggleFinalState('venue', venue.id, expense.id)}
                            />
                        )}
                    {!!expense.amount.length &&
                        expense.amount.map(
                            (quantity, i) =>
                                quantity.name && (
                                    <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full" key={i}>
                                        <div className="tw-col-span-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                            <label className="text-input__label tw-text-xs">{quantity.name}</label>
                                            {props.selectable ? (
                                                <FloatInput
                                                    containerClasses="tw-w-full"
                                                    variant="white"
                                                    value={
                                                        Object.prototype.hasOwnProperty.call(assignedValues, quantity.name) ? assignedValues[quantity.name] : 0
                                                    }
                                                    prefix="#"
                                                    expenseKey={expenseIndex}
                                                    venueId={venue.id}
                                                    quantityName={quantity.name}
                                                    price={expense.price}
                                                    handleQuantityChange={props.handleQuantityChange}
                                                    disabled={
                                                        !props.editableTable ||
                                                        props.finalStates.venues[venue.id].isDisabled ||
                                                        props.finalStates.expenses[expense.id].isDisabled ||
                                                        props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled ||
                                                        !props.finalStates.venues[venue.id].isSelected ||
                                                        !props.finalStates.expenses[expense.id].isSelected ||
                                                        !props.finalStates.expenses[expense.id].venues[venue.id]?.isSelected ||
                                                        props.forProcurement
                                                    }
                                                />
                                            ) : (
                                                <FloatInput
                                                    containerClasses="tw-w-full"
                                                    variant="white"
                                                    value={
                                                        Object.prototype.hasOwnProperty.call(assignedValues, quantity.name) ? assignedValues[quantity.name] : 0
                                                    }
                                                    prefix="#"
                                                    expenseKey={expenseIndex}
                                                    venueId={venue.id}
                                                    quantityName={quantity.name}
                                                    price={expense.price}
                                                    handleQuantityChange={props.handleQuantityChange}
                                                    disabled={!props.editableTable || props.forProcurement}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ),
                        )}
                    <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full">
                        {Object.prototype.hasOwnProperty.call(assignedValues, 'Price') ? (
                            <div className="tw-col-span-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                <label className="text-input__label tw-text-xs">{$t('Price')}</label>
                                {props.selectable ? (
                                    <FloatInput
                                        containerClasses="tw-w-full"
                                        variant="white"
                                        value={assignedValues.Price}
                                        expenseKey={expenseIndex}
                                        venueId={venue.id}
                                        quantityName={'Price'}
                                        price={expense.price}
                                        handleQuantityChange={props.handleQuantityChange}
                                        thousandSeparator={true}
                                        disabled={
                                            !props.editableTable ||
                                            props.finalStates.venues[venue.id].isDisabled ||
                                            props.finalStates.expenses[expense.id].isDisabled ||
                                            props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled ||
                                            !props.finalStates.venues[venue.id].isSelected ||
                                            !props.finalStates.expenses[expense.id].isSelected ||
                                            !props.finalStates.expenses[expense.id].venues[venue.id]?.isSelected ||
                                            props.forProcurement
                                        }
                                        suffix={
                                            props.editableTable && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => props.handleQuantityChange(false, expenseIndex, venue.id, 'Price', expense.price)}
                                                >
                                                    <TrashIcon className="tw-w-3 tw-h-3 tw-text-red" />
                                                </button>
                                            )
                                        }
                                    />
                                ) : (
                                    <FloatInput
                                        containerClasses="tw-w-full"
                                        variant="white"
                                        value={assignedValues.Price}
                                        expenseKey={expenseIndex}
                                        venueId={venue.id}
                                        quantityName={'Price'}
                                        price={expense.price}
                                        handleQuantityChange={props.handleQuantityChange}
                                        thousandSeparator={true}
                                        disabled={!props.editableTable || props.forProcurement}
                                        suffix={
                                            props.editableTable && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => props.handleQuantityChange(false, expenseIndex, venue.id, 'Price', expense.price)}
                                                >
                                                    <TrashIcon className="tw-w-3 tw-h-3 tw-text-red" />
                                                </button>
                                            )
                                        }
                                    />
                                )}
                            </div>
                        ) : props.editableTable && !props.forProcurement ? (
                            <div className="tw-col-span-3">
                                {props.selectable ? (
                                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                        <TextButton
                                            variant="primary"
                                            onClick={(e) =>
                                                props.handleQuantityChange(
                                                    expense.price === 0 ? '0' : expense.price,
                                                    expenseIndex,
                                                    venue.id,
                                                    'Price',
                                                    expense.price,
                                                )
                                            }
                                            disabled={
                                                props.finalStates.venues[venue.id].isDisabled ||
                                                props.finalStates.expenses[expense.id].isDisabled ||
                                                props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled ||
                                                !props.finalStates.venues[venue.id].isSelected ||
                                                !props.finalStates.expenses[expense.id].isSelected ||
                                                !props.finalStates.expenses[expense.id].venues[venue.id]?.isSelected
                                            }
                                        >
                                            {$t('Set custom price')}
                                        </TextButton>
                                    </div>
                                ) : (
                                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                        <TextButton
                                            variant="primary"
                                            onClick={(e) =>
                                                props.handleQuantityChange(
                                                    expense.price === 0 ? '0' : expense.price,
                                                    expenseIndex,
                                                    venue.id,
                                                    'Price',
                                                    expense.price,
                                                )
                                            }
                                        >
                                            {$t('Set custom price')}
                                        </TextButton>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="tw-col-span-3 tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                <label className="text-input__label tw-text-xs">{$t('Price')}</label>
                                <div className="tw-flex tw-items-center tw-h-7 tw-rounded-lg tw-text-xs tw-px-2 tw-py-1 tw-bg-white tw-text-charcoal tw-font-semibold">
                                    {formatFigure(expense.price)}
                                </div>
                            </div>
                        )}
                    </div>
                    {showAnyTotals && (
                        <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full">
                            {props.showTotal && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                    <div>{$t('Budget')}</div>
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id)
                                                ? props.totals.expenses[expenseIndex].venues[venue.id].budget.total
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                            {props.showUncommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                    <div>{$t('Uncommitted')}</div>
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id)
                                                ? props.totals.expenses[expenseIndex].venues[venue.id].uncommitted.total
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                            {props.showCommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                    <div className="tw-flex tw-gap-x-2 tw-items-center">
                                        <div>{$t('Committed')}</div>
                                        {Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id) &&
                                            props.totals.expenses[expenseIndex].venues[venue.id].committed.total > 0 && (
                                                <div
                                                    className="tw-cursor-pointer"
                                                    onClick={(e) => props.showCommitmentModal(expense.committed_expenditures, venue.id, expenseableId)}
                                                >
                                                    <HelpCircleIcon></HelpCircleIcon>
                                                </div>
                                            )}
                                    </div>
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id)
                                                ? props.totals.expenses[expenseIndex].venues[venue.id].committed.total
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const cellRenderer = useCallback(
        ({ columnIndex, rowIndex, key, style }) => {
            // ─── Fixed expense-header row ───
            if (rowIndex === 0) {
                if (columnIndex === 0) {
                    return renderHeaderParentCell(key, style);
                }
                const col = columns[columnIndex - 1];
                if (!col) return <div key={key} style={style} />;
                if (col.isDivider) {
                    return renderHeaderDividerCell(key, style, col.expense, col.expenseIndex);
                }
                return renderHeaderExpenseCell(key, style, col.expense, col.expenseIndex);
            }

            const venue = venues[rowIndex - 1];
            if (!venue) return <div key={key} style={style} />;

            // ─── Fixed venue-name column ───
            if (columnIndex === 0) {
                return renderVenueFixedCell(key, style, venue);
            }

            const col = columns[columnIndex - 1];
            if (!col) return <div key={key} style={style} />;

            // Divider columns scroll normally as narrow charcoal strips (no interior pinning).
            if (col.isDivider) {
                return (
                    <div
                        key={key}
                        style={style}
                        className={classNames('!tw-bg-charcoal-50 !tw-border-r-charcoal-50 tw-border-r-2', { 'tw-border-t-2 tw-border-grey': rowIndex > 1 })}
                    />
                );
            }

            return renderExpenseDataCell(key, style, col.expense, col.expenseIndex, venue);
        },
        [
            columns,
            venues,
            props.totals,
            props.finalStates,
            props.showExpense,
            props.showTotal,
            props.showUncommitted,
            props.showCommitted,
            props.selectable,
            props.editableTable,
            props.forProcurement,
            tooltipParentEl,
        ],
    );

    return (
        // Height strategy: this grid renders full-page AND inside two modals
        // (ProcurementExpenseEditModal / UncommittedExpendituresEditModal). The modal body
        // (.modal__inner-container) is NOT independently scrollable — scroll happens on the
        // outer .modal__box (overflow-y-auto + max-h-full). So we cap to the available space
        // (tw-max-h-full) and let the modal scroll any overflow, rather than forcing a hard
        // 72vh box that could spill past the modal and clip under !tw-overflow-hidden. The
        // min-height keeps the virtualised grid usable on short viewports.
        <div ref={tableWrapperEl} className="inbud-table_wrapper !tw-overflow-hidden tw-h-[72vh] tw-max-h-full tw-min-h-[24rem]">
            <div className="tw-relative tw-h-full tw-w-full">
                <AutoSizer>
                    {({ width, height }) => (
                        <MultiGrid
                            ref={gridRef}
                            width={width}
                            height={height}
                            rowHeight={getRowHeight}
                            rowCount={rowCount}
                            columnWidth={getColumnWidth}
                            columnCount={columnCount}
                            fixedColumnCount={1}
                            fixedRowCount={1}
                            cellRenderer={cellRenderer}
                            styleTopRightGrid={{ backgroundColor: '#C1D7FD' }}
                            styleTopLeftGrid={{ backgroundColor: '#C1D7FD' }}
                            // Pass-through props below are not used by MultiGrid internally —
                            // they force PureComponent to re-render when cell data changes.
                            columns={columns}
                            totals={props.totals}
                            finalStates={props.finalStates}
                            showTotal={props.showTotal}
                            showUncommitted={props.showUncommitted}
                            showCommitted={props.showCommitted}
                        />
                    )}
                </AutoSizer>
            </div>
        </div>
    );
}
